/**
 * Readiness score calculation logic
 * Shared between web and mobile applications
 * Ported from web AuthProvider to ensure consistency
 */

import { computeQuestionPools } from './questionPools';

/**
 * Calculate readiness scores based on question scores, questions list, and app domains
 * @param {Object[]} scores - Array of question score objects
 * @param {Object[]} questionsList - Array of all questions
 * @param {Object[]} appDomains - Array of domain/topic objects
 * @returns {Object} Readiness scores with overall and per-topic breakdown
 */
export const calculateReadinessScores = (scores, questionsList, appDomains) => {
    if (!scores || !Array.isArray(scores) || scores.length === 0) {
        return createEmptyReadinessScores(appDomains);
    }

    if (!questionsList || !Array.isArray(questionsList) || questionsList.length === 0) {
        return createEmptyReadinessScores(appDomains);
    }

    if (!appDomains || !Array.isArray(appDomains) || appDomains.length === 0) {
        return createEmptyReadinessScores([]);
    }

    // Counts are derived from the shared, bundle-anchored, type-insensitive classifier so the
    // dashboard agrees with the custom-test builder: a question is "answered" only when a real
    // bundled question has a score record with times_answered >= 1. Orphan records (answers to
    // questions retired from the bundle) and string-typed topic_ids are handled correctly.
    let overall = { total: 0, answered: 0, correct: 0, accuracySum: 0 };
    var topicProgress = [];
    for (let index in appDomains) {
        const domain = appDomains[index];
        const p = computeQuestionPools(questionsList, scores, [domain.id]);
        const topic_accuracy_score_percentage = p.answeredCount > 0 ? p.accuracySum / p.answeredCount : 0;
        const topic_completeness_score_percentage = p.total > 0 ? p.answeredCount / p.total : 0;
        const readiness_score = (topic_accuracy_score_percentage * 0.7 + topic_completeness_score_percentage * 0.3) * 100;

        topicProgress.push({
            domain_name: domain.domain_name,
            domain_id: domain.id,
            total_questions: p.total,
            questions_answered: p.answeredCount,
            questions_correct: p.correctCount,
            questions_incorrect: p.incorrectCount,
            total_accuracy_score: p.accuracySum,
            accuracy_score_percentage: topic_accuracy_score_percentage,
            completeness_score_percentage: topic_completeness_score_percentage,
            readiness_score: isNaN(readiness_score) ? 0 : readiness_score,
        });

        overall.total += p.total;
        overall.answered += p.answeredCount;
        overall.correct += p.correctCount;
        overall.accuracySum += p.accuracySum;
    }

    const accuracy_score_percentage = overall.answered > 0 ? overall.accuracySum / overall.answered : 0;
    const completeness_score_percentage = overall.total > 0 ? overall.answered / overall.total : 0;

    return {
        total_questions: overall.total,
        questions_answered: overall.answered,
        questions_correct: overall.correct,
        questions_incorrect: overall.answered - overall.correct,
        total_accuracy_score: overall.accuracySum,
        accuracy_score_percentage: accuracy_score_percentage,
        completeness_score_percentage: completeness_score_percentage,
        readiness_score: isNaN(accuracy_score_percentage) || isNaN(completeness_score_percentage)
            ? 0
            : (accuracy_score_percentage * 0.7 + completeness_score_percentage * 0.3) * 100,
        topic_progress: topicProgress,
    };
};

/**
 * Creates empty readiness scores structure
 * @param {Object[]} appDomains - Array of domain/topic objects
 * @returns {Object} Empty readiness scores structure
 */
const createEmptyReadinessScores = (appDomains = []) => {
    const topicProgress = appDomains.map(domain => ({
        domain_name: domain.domain_name || '',
        domain_id: domain.id || 0,
        total_questions: 0,
        questions_answered: 0,
        questions_correct: 0,
        questions_incorrect: 0,
        total_accuracy_score: 0,
        accuracy_score_percentage: 0,
        completeness_score_percentage: 0,
        readiness_score: 0,
    }));

    return {
        total_questions: 0,
        questions_answered: 0,
        questions_correct: 0,
        questions_incorrect: 0,
        total_accuracy_score: 0,
        accuracy_score_percentage: 0,
        completeness_score_percentage: 0,
        readiness_score: 0,
        topic_progress: topicProgress,
    };
};

/**
 * Calculate topic-specific readiness score
 * @param {Object[]} scores - Array of question score objects
 * @param {Object[]} questionsList - Array of all questions
 * @param {number} topicId - Specific topic ID to calculate for
 * @returns {Object} Topic-specific readiness score
 */
export const calculateTopicReadinessScore = (scores, questionsList, topicId) => {
    if (!scores || !questionsList || !topicId) {
        return {
            total_questions: 0,
            questions_answered: 0,
            questions_correct: 0,
            readiness_score: 0
        };
    }

    const topic_total = questionsList.filter(e => e.topic_id === topicId).length;
    const topic_score_list = scores.filter(i => i.topic_id === topicId);
    const topic_questions_answered = topic_score_list.filter(i => i.times_answered >= 1).length;
    const topic_questions_correct = topic_score_list.filter(i => i.score >= 1).length;
    const topic_total_accuracy_score = topic_score_list.reduce((sum, s) => sum + (s.accuracy_score ?? 0), 0);
    const topic_accuracy_score_percentage = topic_questions_answered > 0 ? topic_total_accuracy_score / topic_questions_answered : 0;
    const topic_completeness_score_percentage = topic_total > 0 ? topic_questions_answered / topic_total : 0;
    const readiness_score = (topic_accuracy_score_percentage * 0.7 + topic_completeness_score_percentage * 0.3) * 100;

    return {
        total_questions: topic_total,
        questions_answered: topic_questions_answered,
        questions_correct: topic_questions_correct,
        questions_incorrect: topic_questions_answered - topic_questions_correct,
        accuracy_score_percentage: topic_accuracy_score_percentage,
        completeness_score_percentage: topic_completeness_score_percentage,
        readiness_score: isNaN(readiness_score) ? 0 : readiness_score,
    };
};

/**
 * Gets readiness level based on score
 * @param {number} readinessScore - Readiness score (0-100)
 * @returns {Object} Level information with color and description
 */
export const getReadinessLevel = (readinessScore) => {
    if (readinessScore >= 85) {
        return {
            level: 'excellent',
            color: '#10b981', // Green
            description: 'Excellent readiness'
        };
    } else if (readinessScore >= 70) {
        return {
            level: 'good',
            color: '#3b82f6', // Blue
            description: 'Good readiness'
        };
    } else if (readinessScore >= 50) {
        return {
            level: 'fair',
            color: '#f59e0b', // Orange
            description: 'Fair readiness'
        };
    } else {
        return {
            level: 'poor',
            color: '#ef4444', // Red
            description: 'Needs improvement'
        };
    }
};